import { useState } from 'react'
import { useRouter } from 'next/router'
import Joyride, { Placement, Step } from 'react-joyride'

import axios from 'axios'
import Cookies from 'js-cookie'
import { addDays, endOfDay, isToday, addMinutes, startOfDay } from 'date-fns'
import { ArrowRight, X } from 'react-feather'
import useSWR, { SWRConfiguration } from 'swr'

import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import LoadingButton from '@mui/lab/LoadingButton'
import { styled } from '@mui/material/styles'

import Step1Content from 'src/components/common/export-component/steps/one'
import Step2Content from 'src/components/common/export-component/steps/two'
import { ExportConfigStep1, Column, DownloadReportConfig } from 'src/components/common/export-component/types'

import { alertSeverites, apiURI, constants, dashboardTabs, permissionType } from 'src/lib/utils/constants'
import { postRequest } from 'src/lib/core/api-client'
import { ParentComponentTypes } from 'src/lib/types/activityLogTypes'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import { useLocale } from 'src/lib/hooks/useLocal'
import { formatDateToUTC, toUTC, toUTCTimeStamp } from 'src/lib/utils/helpers'
import { exportReport } from 'src/lib/core/api-methods'

import JoyrideTooltip from 'src/components/common/export-component/steps/Tooltip'
import { useUser } from 'src/lib/contexts/UserContext'
import PermissionService from 'src/services/PermissionService'
import { useACRPermissions } from 'src/lib/access-permission-hooks/useACRPermissions'
import { useOrders } from 'src/lib/access-permission-hooks/useOrderPermissions'

const fetcher: SWRConfiguration<Column[]>['fetcher'] = async (url: string) => {
  const response = await axios.get(url, {
    baseURL: apiURI.CHECKOUT_NODE,
    headers: { Authorization: Cookies.get(constants.AUTH_COOKIE_CLIENT) ?? '' }
  })
  return response.data.data.columns
}

const StyledDialog = styled(Dialog)(() => ({
  '& .MuiPaper-root': {
    borderRadius: '12px'
  },
  '& .MuiDialogContent-root': {
    padding: '0px 16px 16px',
    minWidth: '600px',
    minHeight: '600px'
  }
}))

const StyledDialogTitle = styled(DialogTitle)(() => ({
  '& .MuiDialogTitle-root': {
    fontSize: 16,
    fontWeight: 600,
    padding: '16px 16px'
  }
}))

const downloadReportSteps: Step[] = [
  {
    target: '.nav-download-report',
    content: 'One new report added',
    disableBeacon: true,
    placement: 'right' as Placement,
    floaterProps: {
      hideArrow: true
    }
  }
]

const ExportConfigurationDialog = ({ type }: { type: ParentComponentTypes }) => {
  const { t } = useLocale()
  const router = useRouter()
  const { user } = useUser()

  const isOrders = router.query['open-report-filter'] === 'orders'
  const isAbandonedCheckout = router.query['open-report-filter'] === 'abandoned-checkout'
  const isAllCheckout = router.query['open-report-filter'] === 'all-checkouts'

  const { canExportACRData } = useACRPermissions()
  const { canExportOrdersData } = useOrders()

  const exportType =
    router.pathname === '/orders'
      ? 'orders'
      : router.pathname === '/abandoned-checkouts'
      ? 'abandoned_checkouts'
      : 'all_checkouts'

  const [downloadReportStepsRun, setDownloadReportStepsRun] = useState(false)

  const [open, setOpen] = useState(isOrders || isAbandonedCheckout || isAllCheckout)
  const [step, setStep] = useState(1)
  const [isStepValid, setIsStepValid] = useState(false)
  const [config, setConfig] = useState<DownloadReportConfig>({
    step1: {
      exportType,
      selectedColumns: []
    },
    step2: {
      exportFrequency: 'once',
      dateRange: {
        startDate: addDays(new Date(), -1).toISOString(),
        endDate: addDays(new Date(), -1).toISOString()
      },
      schedule: {
        frequency: 'daily',
        hour: 0,
        day: 0,
        cronExpression: '0 0 * * ? *',
        scheduledStartDate: new Date().toISOString()
      },
      emails: [''],
      reportName: ''
    }
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const isExportingTodaysReport = config.step1.exportType === 'today'

  const { data, error, isValidating } = useSWR<Column[]>(
    isExportingTodaysReport ? null : `dashboard/report/column-list?type=${config.step1.exportType}`,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnMount: true
    }
  )

  const handleTodayExport = async () => {
    try {
      if (!config.step2.email) {
        errorToast(t('report.download.failed'))
        return
      }
      setIsSubmitting(true)
      const response: any = await exportReport(
        exportType === 'abandoned_checkouts' ? 'all_checkouts' : exportType,
        config.step2.email,
        toUTCTimeStamp(startOfDay(new Date())),
        toUTCTimeStamp(endOfDay(new Date()))
      )
      if (response?.error) {
        setIsSubmitting(false)
        return
      }
      successToast(t('report.download.success.today'))
      resetState()
    } catch (error) {
      errorToast(t('report.download.failed'))
      console.error(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const resetState = () => {
    setStep(1)
    setIsStepValid(false)
    setConfig({
      step1: {
        exportType:
          router.pathname === '/orders'
            ? 'orders'
            : router.pathname === '/abandoned-checkouts'
            ? 'abandoned_checkouts'
            : 'all_checkouts',
        selectedColumns: []
      },
      step2: {
        exportFrequency: 'once',
        dateRange: {
          startDate: addDays(new Date(), -1).toISOString(),
          endDate: new Date().toISOString()
        },
        schedule: {
          frequency: 'daily',
          hour: 0,
          day: 0,
          cronExpression: '0 0 * * ? *',
          scheduledStartDate: new Date().toISOString()
        },
        emails: [''],
        reportName: ''
      }
    })
  }

  const handleSubmit = async () => {
    if (isExportingTodaysReport) {
      await handleTodayExport()
      return
    }

    try {
      setIsSubmitting(true)
      const payload: {
        name: string
        frequencyType: string
        columns: readonly string[]
        type: ExportConfigStep1['exportType']
        reportDateRange?: {
          start: string
          end: string
        }
        emails?: string[]
        scheduleStartDate?: string
        frequency?: string
      } = {
        name: config.step2.reportName,
        frequencyType: config.step2.exportFrequency.toUpperCase(),
        columns: config.step1.selectedColumns,
        type: config.step1.exportType
      }

      // handle emails
      if (config.step2.emails.length > 0) {
        payload.emails = config.step2.emails
      }

      // handle date range for once
      if (
        config.step2.exportFrequency === 'once' &&
        config.step2.dateRange.startDate &&
        config.step2.dateRange.endDate
      ) {
        payload.reportDateRange = {
          start: formatDateToUTC(addDays(new Date(config.step2.dateRange.startDate), -1)),
          end: formatDateToUTC(config.step2.dateRange.endDate)
        }
      }

      // handle recurring schedule
      if (config.step2.exportFrequency === 'recurring' && config.step2.schedule?.scheduledStartDate) {
        const scheduledDate = new Date(config.step2.schedule.scheduledStartDate)

        if (isToday(scheduledDate)) {
          // If scheduled date is today, set time to current time + 5 minutes
          const futureDate = addMinutes(new Date(), 5)
          payload.scheduleStartDate = toUTC(futureDate.toISOString())
        } else {
          payload.scheduleStartDate = toUTC(config.step2.schedule.scheduledStartDate)
        }

        payload.frequency = config.step2.schedule?.cronExpression
      }

      await postRequest(`/dashboard/report/scheduler-create`, apiURI.CHECKOUT_NODE, payload)
      successToast(t('report.download.success'))
      setDownloadReportStepsRun(true)

      // reset the states
      resetState()
    } catch (error) {
      errorToast(t('report.download.failed'))
      console.error(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleNext = async () => {
    if (!isStepValid && !isExportingTodaysReport) return

    if (step === 1) {
      setStep(2)
    } else {
      await handleSubmit()
      setOpen(false)
    }
  }

  const handleBack = () => {
    if (step === 2) {
      setStep(1)
    } else {
      setOpen(false)
    }
  }

  const isExportDisabled =
    (type === dashboardTabs.FLO_CHECKOUT_TAB && !canExportACRData) ||
    (type === dashboardTabs.FLO_ORDERS_TAB && !canExportOrdersData)

  if (isExportDisabled) {
    return null
  }

  return (
    <PermissionService permissionId={permissionType.SHOW_PII_DATA} hideElement={true}>
      <>
        <Joyride
          run={downloadReportStepsRun}
          steps={downloadReportSteps}
          disableOverlay
          tooltipComponent={props => <JoyrideTooltip {...props} onDismiss={() => setDownloadReportStepsRun(false)} />}
        />
        <LoadingButton
          loading={isValidating}
          disabled={!isExportingTodaysReport && (!data || error)}
          id='export-button'
          variant='outlined'
          size='medium'
          style={{ whiteSpace: 'nowrap', height: 36, background: '#FFFFFF' }}
          onClick={() => setOpen(true)}
          endIcon={<ArrowRight className='w-4 h-4' id='export-button-icon' />}
        >
          {t('report.download.title')}
        </LoadingButton>

        <StyledDialog open={open} onClose={() => setOpen(false)} maxWidth='md'>
          <StyledDialogTitle
            sx={{
              padding: '16px 16px',
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
              zIndex: 1000,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <div className='flex-col justify-between items-center'>
              <div className='text-base font-semibold'>
                {step === 1 ? 'Select columns to export' : 'Finalise export'}
              </div>
              <p className='text-sm text-gray-500'>Step {step} of 2</p>
            </div>
            <div className='flex'>
              <button onClick={() => setOpen(false)}>
                <X className='w-4 h-4' />
              </button>
            </div>
          </StyledDialogTitle>
          <DialogContent sx={{ padding: '16px 16px' }}>
            {step === 1 ? (
              <Step1Content
                isLoading={isValidating}
                columns={data ?? []}
                config={config.step1}
                onChange={newConfig => setConfig(prev => ({ ...prev, step1: newConfig }))}
                onValidationChange={setIsStepValid}
              />
            ) : (
              <Step2Content
                step1Config={config.step1}
                config={config.step2}
                onChange={newConfig => setConfig(prev => ({ ...prev, step2: newConfig }))}
                onValidationChange={setIsStepValid}
              />
            )}
          </DialogContent>
          <DialogActions
            sx={{
              padding: '16px 16px',
              position: 'sticky',
              bottom: 0,
              backgroundColor: 'white',
              zIndex: 1000,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Button onClick={handleBack}>{step === 1 ? 'Cancel' : 'Go back'}</Button>
            <LoadingButton
              id='get-report-button'
              variant='contained'
              loadingPosition='end'
              loading={isSubmitting}
              onClick={handleNext}
              disabled={!isStepValid || user?.isDemoUser}
              type={step === 1 ? 'button' : 'submit'}
            >
              {step === 1 ? 'Continue' : 'Get report'}
            </LoadingButton>
          </DialogActions>
        </StyledDialog>
      </>
    </PermissionService>
  )
}

export default ExportConfigurationDialog
